<template>
    <b-card class="sa-card" header-tag="header" footer-tag="footer" title="">
        <template #header>
            <div class="d-flex justify-content-between align-items-center">
                <h5 class="mt-0 mb-0">
                    <span>
                        <i class="fas fa-capsules"></i>
                        {{ titolo }}
                    </span>
                </h5>

                <b-button size="sm" variant="success" :disabled="!currentIdPaziente" @click="onAggiungiTerapia">
                    <b-icon icon="plus"></b-icon>
                    Aggiungi
                </b-button>
            </div>
        </template>

        <div v-if="loading" class="text-center py-3">
            <b-spinner small variant="info"></b-spinner>
            Caricamento terapie...
        </div>

        <template v-else>
            <ul v-if="terapia.length > 0" class="list-unstyled mb-3">
                <li v-for="item in terapia" :key="item.id" class="d-flex justify-content-between align-items-start border-bottom py-2">
                    <div class="flex-grow-1">
                        <div>
                            <strong v-if="item.posologia">
                                {{ item.posologia }}
                            </strong>

                            <strong class="ml-1">
                                {{ item.farmaco }}
                            </strong>
                        </div>

                        <div v-if="item.frequenza || item.descrizioneFrequenza" class="text-danger">
                            {{ item.frequenza || getFrequenzaLabel(item) }}
                        </div>

                        <div class="text-danger">
                            {{ startTherapy() }}
                            {{ formatDate(item.dataInizio) }}
                        </div>

                        <div v-if="item.tempo" class="text-danger">
                            {{ item.tempo }}
                        </div>

                        <i v-if="item.nomeMedico || item.cognomeMedico" class="blockquote-footer"> {{ item.nomeMedico }} {{ item.cognomeMedico }} </i>
                    </div>

                    <!--  <b-button-group size="sm" class="ml-2"> -->
                    <b-button variant="outline-info" v-b-tooltip.hover title="Visualizza terapia" @click="onVisualizzaTerapia(item)">
                        <b-icon icon="eye"></b-icon>
                    </b-button>

                    <b-button variant="outline-warning" v-b-tooltip.hover title="Modifica terapia" @click="onModificaTerapia(item)">
                        <b-icon icon="pencil"></b-icon>
                    </b-button>
                    <!--  </b-button-group> -->
                </li>
            </ul>

            <div v-else class="text-center text-muted py-3">Nessuna terapia presente.</div>
        </template>

        <!-- Modale visualizzazione terapia -->
        <b-modal ref="modalVisualizzaTerapia" title="Visualizza Terapia" size="lg" centered scrollable hide-footer @hidden="onChiudiVisualizzazione">
            <paziente-terapia-view-farmaco-component v-if="terapiaVisualizzata" :jsonData="terapiaVisualizzata"></paziente-terapia-view-farmaco-component>
        </b-modal>

        <!-- Modale inserimento e modifica terapia -->
        <b-modal ref="modalFormTerapia" :title="titoloModalTerapia" size="lg" centered scrollable hide-footer @hidden="onChiudiForm">
            <paziente-terapia-edit-farmaco-component v-if="showFormTerapia" :idPaziente="currentIdPaziente" :terapia="terapiaModifica" @update="onUpdateTerapia"></paziente-terapia-edit-farmaco-component>
        </b-modal>
    </b-card>
</template>

<script>
import axios from "axios";
import moment from "moment";

import PazienteTerapiaEditFarmacoComponent from "../terapia/components/PazienteTerapiaEditFarmacoComponent.vue";
import PazienteTerapiaViewFarmacoComponent from "../terapia/components/PazienteTerapiaViewFarmacoComponent.vue";

export default {
    components: {
        PazienteTerapiaEditFarmacoComponent,
        PazienteTerapiaViewFarmacoComponent,
    },

    props: {
        idPaziente: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            pathResource: "/pazienteterapie",
            terapia: [],
            loading: false,
            terapiaVisualizzata: null,
            terapiaModifica: null,
            showFormTerapia: false,
        };
    },

    computed: {
        titolo() {
            return this.$i18n.t("patient.menu.therapy");
        },

        currentIdPaziente() {
            return this.idPaziente || this.$route.query.idPaziente || this.$route.params.id || null;
        },

        titoloModalTerapia() {
            return this.terapiaModifica ? "Modifica Terapia" : "Aggiungi Terapia";
        },
    },

    watch: {
        currentIdPaziente: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.loadTerapie();
                } else {
                    this.terapia = [];
                }
            },
        },
    },

    methods: {
        loadTerapie() {
            if (!this.currentIdPaziente) {
                this.terapia = [];
                return;
            }

            let me = this;
            let link = process.env.VUE_APP_PATH_API + me.pathResource;

            me.loading = true;

            axios
                .get(link, {
                    params: {
                        idPaziente: me.currentIdPaziente,
                    },
                })
                .then((response) => {
                    const data = response.data.data;

                    me.terapia = Array.isArray(data) ? data : [];
                })
                .catch((error) => {
                    me.terapia = [];

                    const messaggio = error.response && error.response.data && error.response.data.messaggio ? error.response.data.messaggio : "Errore durante il caricamento delle terapie.";

                    me.$bvToast.toast(messaggio, {
                        title: "ATTENZIONE",
                        variant: "danger",
                        solid: true,
                    });
                })
                .finally(() => {
                    me.loading = false;
                });
        },

        onAggiungiTerapia() {
            if (!this.currentIdPaziente) {
                return;
            }

            this.terapiaModifica = null;
            this.showFormTerapia = true;

            this.$nextTick(() => {
                this.$refs.modalFormTerapia.show();
            });
        },

        onVisualizzaTerapia(item) {
            this.terapiaVisualizzata = Object.assign({}, item, {
                orarioSomministrazione: item.orarioSomministrazione || [],
            });

            this.$nextTick(() => {
                this.$refs.modalVisualizzaTerapia.show();
            });
        },

        onModificaTerapia(item) {
            if (!this.currentIdPaziente) {
                return;
            }

            this.terapiaModifica = Object.assign({}, item, {
                idPaziente: this.currentIdPaziente,
                orarioSomministrazione: Array.isArray(item.orarioSomministrazione) ? item.orarioSomministrazione.slice() : item.orarioSomministrazione || [],
            });

            this.showFormTerapia = true;

            this.$nextTick(() => {
                this.$refs.modalFormTerapia.show();
            });
        },

        onUpdateTerapia(response) {
            this.$refs.modalFormTerapia.hide();

            if (response === null) {
                return;
            }

            this.loadTerapie();

            const messaggio = response && response.messaggio ? response.messaggio : "Terapia salvata correttamente.";

            this.$bvToast.toast(messaggio, {
                title: "",
                variant: "success",
                solid: true,
            });
        },

        onChiudiForm() {
            this.showFormTerapia = false;
            this.terapiaModifica = null;
        },

        onChiudiVisualizzazione() {
            this.terapiaVisualizzata = null;
        },

        getFrequenzaLabel(item) {
            return item.descrizioneFrequenza ? this.$i18n.t(item.descrizioneFrequenza) : "";
        },

        formatDate(value) {
            if (value) {
                return moment(new Date(value)).format("DD-MM-YYYY");
            }

            return "---";
        },

        startTherapy() {
            return this.$i18n.t("patient.lblStartTherapy");
        },
    },
};
</script>

<style scoped>
.sa-page-paziente-terapia-attiva .card-body {
    overflow: auto;
}
</style>
